import { GPC_METHODOLOGY_LOOKUP_MAP } from "./methodology-lookup";
/**
 * GPC IPPU sector (Industrial Processes and Product Use) — activity-based
 * emission factors. IPPU is one of the GPC "calculated" sectors: emissions are
 * `activity × factor` over a 2D (category × subsector) grid. The shared logic
 * lives in calculated-sector.ts; this module is just IPPU's data.
 *
 * Unlike energy sectors (one factor per energy source, identical across every
 * subsector), IPPU factors are 2D: an activity category emits only under the
 * subsector it belongs to (process categories under Industrielle Prozesse,
 * product categories under Produktverwendung).
 *
 * Method (v1, "linear" tier):
 *   emission [kg CO₂e] = activity × factor
 * where activity is in tonnes and the factor below is the *combined* CO₂e factor
 * in **kg CO₂e per unit** — it already bundles each gas's per-activity yield ×
 * that gas's GWP. We store the factor in kg-per-unit so the engine's generic
 * `activity × factor` lands in the same kg-CO₂e scale as the energy sectors.
 * Display layers convert kg → t.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * Provenance of the v1 defaults (see IPPU_DEFAULT_PROVENANCE):
 *   - Industrielle Prozesse process CO₂ (cement, lime, glass, ammonia, steel) are
 *     genuine IPCC 2006 Guidelines Vol. 3 Tier-1 defaults — see the per-cell
 *     comments for the chapter/table and units. Cement is per tonne *clinker*.
 *   - Ceramics has no per-tonne-of-product default in the 2006 GL (it is only a
 *     carbonate-consumption category there); the value is a documented estimate
 *     from the 2019 Refinement / EFDB, labelled "Annahme".
 *   - The CHEMIE N₂O term (nitric-acid co-production) and every Produktverwendung
 *     value (F-gases, solvents/lubricants) are documented ARK estimates, not IPCC
 *     per-product factors — IPCC bases F-gases on the mass of the gas itself, and
 *     gives no CO₂ default for solvents. They are kept on the product-mass basis
 *     with an explicit leakage assumption and flagged "Annahme" / quality "L".
 * All defaults remain quality "L" (generic, no city-specific adaptation) and are
 * editable per inventory.
 * ─────────────────────────────────────────────────────────────────────────────
 */
/**
 * 100-year global warming potentials used to convert each gas's mass to CO₂e.
 *
 * CO₂ (1), N₂O (273) and SF₆ (25200) are the real IPCC AR6 values. HFKW (HFCs) and
 * FKW (PFCs) are *substance classes*, not single gases, so they have no one GWP —
 * the values here are representative placeholders ("Annahme") so the bucket behaves
 * like a real gas (mass × GWP = CO₂e) until ARK refines them or splits the class
 * into specific substances. Constants — never user-editable in the factor table.
 */
export const IPPU_GWP_AR6 = {
    CO2: 1,
    N2O: 273,
    HFKW: 1500, // Annahme — repräsentativer HFKW-/HFC-Kältemittelmix
    FKW: 7000, // Annahme — repräsentativer FKW-/PFC-Mix
    SF6: 25200, // IPCC AR6, 100-Jahre
};
/**
 * The greenhouse gases the IPPU factor table breaks down into — the per-gas
 * editing dimension. The F-gas bucket is split into the three German reporting
 * species (HFKW/FKW/SF₆) so each shows a real mass and converts to CO₂e via its
 * own GWP.
 */
export const IPPU_FACTOR_GASES = ["CO2", "N2O", "HFKW", "FKW", "SF6"];
/** The fluorinated-gas species (subset of IPPU_FACTOR_GASES). */
export const IPPU_FGAS_SPECIES = ["HFKW", "FKW", "SF6"];
export const IPPU_GAS_GWP = {
    CO2: IPPU_GWP_AR6.CO2,
    N2O: IPPU_GWP_AR6.N2O,
    HFKW: IPPU_GWP_AR6.HFKW,
    FKW: IPPU_GWP_AR6.FKW,
    SF6: IPPU_GWP_AR6.SF6,
};
/** Short gas labels for the per-gas breakdown rows in the UI. */
const IPPU_GAS_LABEL = {
    CO2: "CO₂",
    N2O: "N₂O",
    HFKW: "HFKW",
    FKW: "FKW",
    SF6: "SF₆",
};
const IPPU_ACTIVITY_UNIT_LABEL = { t: "t" };
const IPPU_FACTOR_UNIT_LABEL = { t: "tCO₂äq/t" };
const { IPPU } = GPC_METHODOLOGY_LOOKUP_MAP.sectors;
// All IPPU activity is in tonnes for v1. Placeholder — some product-use rows may
// later move to a substance-mass or CO₂e basis.
const IPPU_ROW_UNIT_BY_KIND = {
    CEMENT: "t",
    LIME: "t",
    GLASS: "t",
    CERAMICS: "t",
    CHEMICALS_AMMONIA: "t",
    METAL_IRON_STEEL: "t",
    REFRIGERANTS_AC: "t",
    FOAMS: "t",
    ELECTRICAL_EQUIPMENT: "t",
    SOLVENTS: "t",
};
/**
 * 2D default factor table, per gas in **kg CO₂e per tonne of activity**. A present
 * entry means the cell is applicable (the user may enter activity there); an
 * absent entry means the (category × subsector) combination is not modelled and
 * the cell is read-only in the UI. Process categories belong to Industrielle
 * Prozesse; product categories to Produktverwendung. See the module header for
 * provenance. Keyed `${categoryKind}|${subsectorKind}`.
 */
const IPPU_DEFAULT_FACTORS = new Map([
    // --- Industrielle Prozesse (process CO₂, plus N₂O for nitric-acid chemistry) ---
    // Process CO₂ values are genuine IPCC 2006 GL Vol. 3 Tier-1 defaults.
    ["CEMENT|INDUSTRIAL_PROCESSES", { CO2: 520 }], // IPCC Ch 2.2 Eq 2.4: 0.52 t CO₂/t *clinker*
    ["LIME|INDUSTRIAL_PROCESSES", { CO2: 750 }], // IPCC Ch 2.3 Tbl 2.4: 0.75 t CO₂/t high-Ca lime
    ["GLASS|INDUSTRIAL_PROCESSES", { CO2: 200 }], // IPCC Ch 2.4 Tbl 2.6: 0.20 t CO₂/t glass (gross, no cullet credit)
    // Ceramics has no per-tonne-of-product default in the 2006 GL — estimate from the
    // 2019 Refinement / EFDB clay-brick range (~0.06–0.12 t/t), midpoint 0.08. "Annahme".
    ["CERAMICS|INDUSTRIAL_PROCESSES", { CO2: 80 }],
    // CO₂: IPCC Ch 3.2 Tbl 3.1 conventional natural-gas reforming ammonia (1.694 t CO₂/t NH₃).
    // N₂O: placeholder for nitric-acid co-production ("Annahme") — a city with significant
    // nitric-acid output should override (real IPCC range 546–2457 kg CO₂e/t HNO₃, Ch 3.3 Tbl 3.3).
    ["CHEMICALS_AMMONIA|INDUSTRIAL_PROCESSES", { CO2: 1694, N2O: 300 }],
    // IPCC Ch 4.2 Tbl 4.1 global-average crude steel (65% BOF / 30% EAF / 5% OHF) = 1.06 t CO₂/t.
    // Route-specific alternatives to override with: pig iron 1350 / BOF steel 1460 / EAF-scrap 80.
    ["METAL_IRON_STEEL|INDUSTRIAL_PROCESSES", { CO2: 1060 }],
    // --- Produktverwendung (F-gases for refrigerants/foams/switchgear; CO₂ for solvents) ---
    // IPCC provides no per-tonne-of-PRODUCT factor for these — it bases F-gases on the
    // mass of the gas itself. Kept on the product-mass basis with a documented leakage
    // assumption: ≈1 kg of the relevant gas emitted per tonne of product/equipment over
    // its service life, valued at the gas's representative GWP (so the per-gas breakdown
    // shows ≈1 kg/t). All "Annahme" — the kg-gas-per-tonne assumption is the key tunable
    // a domain expert should validate. The per-gas mass in the breakdown is value ÷ GWP.
    ["REFRIGERANTS_AC|PRODUCT_USE", { HFKW: 1500 }], // 1 kg HFKW/t × bucket GWP 1500
    ["FOAMS|PRODUCT_USE", { HFKW: 1300 }], // ≈0.87 kg HFKW/t × 1500 (lower-GWP foam agent)
    ["ELECTRICAL_EQUIPMENT|PRODUCT_USE", { SF6: 25200 }], // 1 kg SF₆/t × SF₆ GWP 25200
    // Solvents have no CO₂ default in IPCC (NMVOC/CO only). Lubricants/waxes basis instead:
    // IPCC Ch 5.2 — 20 kg C/GJ × ODU 0.2 × 44/12 ≈ 635 kg CO₂/t consumed ("Annahme", ODU assumption).
    ["SOLVENTS|PRODUCT_USE", { CO2: 635 }],
]);
// Genuine IPCC 2006 Guidelines Vol. 3 (IPPU) Tier-1 default; CO₂e uses AR6 GWP.
const PROVENANCE_IPCC_2006 = {
    dataSource: "IPCC 2006 GL",
    referenceYear: 2006,
    quality: "L",
};
// ARK modeling assumption — a plausible default not traceable to a published IPCC
// per-product table (ceramics, nitric-acid N₂O, all F-gas product use, solvents).
const PROVENANCE_ESTIMATE = {
    dataSource: "Annahme",
    referenceYear: null,
    quality: "L",
};
const IPPU_DEFAULT_PROVENANCE = new Map([
    // Industrielle Prozesse process CO₂ — genuine IPCC 2006 Vol. 3 (per cell).
    ["CEMENT|INDUSTRIAL_PROCESSES|CO2", PROVENANCE_IPCC_2006],
    ["LIME|INDUSTRIAL_PROCESSES|CO2", PROVENANCE_IPCC_2006],
    ["GLASS|INDUSTRIAL_PROCESSES|CO2", PROVENANCE_IPCC_2006],
    ["CHEMICALS_AMMONIA|INDUSTRIAL_PROCESSES|CO2", PROVENANCE_IPCC_2006],
    ["METAL_IRON_STEEL|INDUSTRIAL_PROCESSES|CO2", PROVENANCE_IPCC_2006],
    // Ceramics CO₂ has no 2006 default — documented estimate (2019 Refinement / EFDB).
    ["CERAMICS|INDUSTRIAL_PROCESSES|CO2", PROVENANCE_ESTIMATE],
    // Nitric-acid co-production N₂O — documented estimate.
    ["INDUSTRIAL_PROCESSES|N2O", PROVENANCE_ESTIMATE],
    // Produktverwendung — F-gases and solvents/lubricants, all documented estimates.
    ["PRODUCT_USE|HFKW", PROVENANCE_ESTIMATE],
    ["PRODUCT_USE|FKW", PROVENANCE_ESTIMATE],
    ["PRODUCT_USE|SF6", PROVENANCE_ESTIMATE],
    ["PRODUCT_USE|CO2", PROVENANCE_ESTIMATE],
]);
/** The IPPU calculated-sector config — drives recompute and the frontend grids. */
export const IPPU_FACTORS = {
    key: "IPPU",
    gases: IPPU_FACTOR_GASES,
    fgasSpecies: IPPU_FGAS_SPECIES,
    gasGwp: IPPU_GAS_GWP,
    gasLabel: IPPU_GAS_LABEL,
    scopes: [1],
    allowsNegativeFactors: false,
    activityUnitLabel: IPPU_ACTIVITY_UNIT_LABEL,
    factorUnitLabel: IPPU_FACTOR_UNIT_LABEL,
    rowUnitByKind: IPPU_ROW_UNIT_BY_KIND,
    defaultFactors: IPPU_DEFAULT_FACTORS,
    defaultProvenance: IPPU_DEFAULT_PROVENANCE,
    categoryKindById: new Map(Object.entries(IPPU.energySources).map(([kind, source]) => [source.id, kind])),
    subsectorKindById: new Map(Object.entries(IPPU.subsectors).map(([kind, subsector]) => [subsector.id, kind])),
};
